import { supabase } from '../lib/supabase';

/**
 * Scheduler Service for Verse Review Distribution
 * Implements intelligent spaced repetition scheduling with configurable windows
 * Based on phase-based intervals to prevent review overload
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

const schedulerConfig = {
  // Scheduling preference: start from minimum day (better retention) or maximum day
  preferMinDay: true, // true = start from windowMinDay, false = start from windowMaxDay
  
  // Optimization scope: reschedule all verses or only those exceeding daily limits
  optimizeAllVerses: true, // true = all verses, false = only overloaded days
  
  // Schedule map range (days from current date)
  scheduleMapDays: 180, // 6 months
};

// Phase intervals (windowMaxDay)
const PHASE_INTERVALS = {
  0: 0,   // Future phase (not scheduled)
  1: 1,   // Phase 1: Daily
  2: 3,   // Phase 2: Every 3 days
  3: 7,   // Phase 3: Weekly
  4: 14,  // Phase 4: Bi-weekly
  5: 30,  // Phase 5: Monthly
  6: 45,  // Phase 6: 45 days
  7: 90,  // Phase 7: Quarterly (Final Phase)
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Calculate window boundaries for a given phase
 * Formula: windowMinDay = windowMaxDay - floor(windowMaxDay / 3)
 */
const calculateWindow = (phase) => {
  const windowMaxDay = PHASE_INTERVALS?.[phase] || 1;
  let windowMinDay = windowMaxDay - Math.floor(windowMaxDay / 3);
  
  return {
    windowMinDay,
    windowMaxDay,
    windowSize: windowMaxDay - windowMinDay + 1,
  };
};

/**
 * Get base date for scheduling calculation
 * Uses last_reviewed_at if available, otherwise created_at
 */
const getBaseDate = (verse) => {
  let baseDate = verse?.lastReviewedAt || verse?.createdAt;
  return new Date(baseDate);
};

/**
 * Add days to a date and return formatted date string (YYYY-MM-DD)
 */
const addDays = (date, days) => {
  const result = new Date(date);
  result?.setDate(result?.getDate() + days);
  return result?.toISOString()?.split('T')?.[0];
};

/**
 * Get today's date as YYYY-MM-DD string (local date, no timezone shift)
 */
const getTodayStr = () => {
  const now = new Date();
  const y = now?.getFullYear();
  const m = String(now?.getMonth() + 1)?.padStart(2, '0');
  const d = String(now?.getDate())?.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Convert snake_case to camelCase
 */
const toCamelCase = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(toCamelCase);
  }
  
  const camelCased = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelCased[camelKey] = toCamelCase(value);
  }
  return camelCased;
};

/**
 * Convert camelCase to snake_case
 */
const toSnakeCase = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(toSnakeCase);
  }
  
  const snakeCased = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    snakeCased[snakeKey] = toSnakeCase(value);
  }
  return snakeCased;
};

// ============================================================================
// CORE SCHEDULING FUNCTIONS
// ============================================================================

/**
 * Get current schedule map for a user
 * Returns a map of dates to verse counts within the specified range
 * 
 * @param {string} userId - User ID
 * @param {Date} startDate - Start date for schedule map
 * @param {Date} endDate - End date for schedule map
 * @returns {Object} Map of date strings to verse counts
 */
const getScheduleMap = async (userId, startDate = new Date(), endDate = null) => {
  try {
    // Default end date to 6 months from start
    if (!endDate) {
      endDate = new Date(startDate);
      endDate?.setDate(endDate?.getDate() + schedulerConfig?.scheduleMapDays);
    }

    const startDateStr = startDate?.toISOString()?.split('T')?.[0];
    const endDateStr = endDate?.toISOString()?.split('T')?.[0];

    // Fetch all verses for user with next_review_date in range
    // Include verses with current_phase > 0 (exclude Future phase)
    const { data: verses, error } = await supabase?.from('verses')?.select('id, next_review_date, verse_count, current_phase')?.eq('user_id', userId)?.gt('current_phase', 0)?.gte('next_review_date', startDateStr)?.lte('next_review_date', endDateStr);

    if (error) {
      console.error('Error fetching schedule map:', error);
      return {};
    }

    // Build schedule map
    const scheduleMap = {};
    
    verses?.forEach(verse => {
      const date = verse?.next_review_date;
      const count = verse?.verse_count || 1;
      
      if (!scheduleMap?.[date]) {
        scheduleMap[date] = 0;
      }
      scheduleMap[date] += count;
    });

    return scheduleMap;
  } catch (error) {
    console.error('Error in getScheduleMap:', error);
    return {};
  }
};

/**
 * Find optimal day within window that has least verses
 * Implements tie-breaking based on preferMinDay configuration
 * Supports windowMinDay = 0 for unreviewed verses starting from current date
 * 
 * @param {Object} scheduleMap - Current schedule map
 * @param {Date} baseDate - Base date for calculation
 * @param {number} windowMinDay - Minimum days from base date (can be 0 for unreviewed verses)
 * @param {number} windowMaxDay - Maximum days from base date
 * @returns {string} Optimal date string (YYYY-MM-DD)
 */
const findOptimalDay = (scheduleMap, baseDate, windowMinDay, windowMaxDay) => {
  // Special case: Phase 1 always schedules exactly 1 day from base (unless unreviewed)
  if (windowMinDay === 1 && windowMaxDay === 1) {
    const candidate = addDays(baseDate, 1);
    const todayStr = getTodayStr();
    // Never schedule in the past
    return candidate >= todayStr ? candidate : addDays(new Date(todayStr), 0);
  }

  const todayStr = getTodayStr();

  // Build array of candidate days with their verse counts
  const candidates = [];
  
  for (let offset = windowMinDay; offset <= windowMaxDay; offset++) {
    const dateStr = addDays(baseDate, offset);
    // Never allow a date in the past
    if (dateStr < todayStr) continue;
    const verseCount = scheduleMap?.[dateStr] || 0;
    
    candidates?.push({
      date: dateStr,
      offset,
      count: verseCount,
    });
  }

  // If all candidates are in the past (shouldn't happen after above filter, but safety net)
  if (candidates?.length === 0) {
    return addDays(new Date(todayStr), windowMaxDay);
  }

  // Find minimum verse count
  const minCount = Math.min(...candidates?.map(c => c?.count));
  
  // Filter candidates with minimum count
  const optimalCandidates = candidates?.filter(c => c?.count === minCount);

  // Tie-breaking logic: prefer dates closer to current date (windowMinDay)
  // This ensures unreviewed verses fill days closer to today first
  if (schedulerConfig?.preferMinDay) {
    // Prefer earliest date (closest to windowMinDay)
    return optimalCandidates?.[0]?.date;
  } else {
    // Prefer latest date (closest to windowMaxDay)
    return optimalCandidates?.[optimalCandidates?.length - 1]?.date;
  }
};

/**
 * Schedule a single verse
 * Calculates optimal next_review_date and updates database
 * For unreviewed verses (last_reviewed_at IS NULL), expands window to currentDate + windowMaxDay
 * to prevent review avalanches for migrating users
 * 
 * @param {string} verseId - Verse ID
 * @param {number} currentPhase - Current phase of the verse
 * @param {Date|string} lastReviewDate - Last review date (or created_at if never reviewed)
 * @param {string} userId - User ID
 * @param {Object} scheduleMap - Current schedule map (will be updated)
 * @param {Object} preloadedVerseData - Already-fetched verse data (camelCase) to skip DB round-trip
 * @returns {Object} Updated verse data
 */
const scheduleVerse = async (verseId, currentPhase, lastReviewDate, userId, scheduleMap = null, preloadedVerseData = null) => {
  try {
    // Get verse data if not provided
    let verse;
    let isUnreviewed = false;
    
    if (preloadedVerseData) {
      // Use pre-fetched data — no DB round-trip needed
      verse = preloadedVerseData;
      currentPhase = verse?.currentPhase;
      lastReviewDate = verse?.lastReviewedAt || verse?.createdAt;
      isUnreviewed = !verse?.lastReviewedAt;
    } else if (!lastReviewDate) {
      const { data, error } = await supabase?.from('verses')?.select('*')?.eq('id', verseId)?.single();

      if (error || !data) {
        console.error('Error fetching verse:', error);
        return { error: 'Verse not found' };
      }

      verse = toCamelCase(data);
      currentPhase = verse?.currentPhase;
      lastReviewDate = verse?.lastReviewedAt || verse?.createdAt;
      
      // Detect unreviewed verses (never been reviewed)
      isUnreviewed = !verse?.lastReviewedAt;
    }

    // Skip Phase 0 (Future)
    if (currentPhase === 0) {
      return { error: 'Cannot schedule Phase 0 verses' };
    }

    // Calculate window
    let { windowMinDay, windowMaxDay } = calculateWindow(currentPhase);
    
    const todayStr = getTodayStr();
    let baseDate;
    
    if (isUnreviewed && currentPhase >= 1 && currentPhase <= 7) {
      // UNREVIEWED VERSE LOGIC: Use current date as base, start from day 0
      baseDate = new Date(todayStr);
      windowMinDay = 0;
    } else {
      // REVIEWED VERSE LOGIC: use last review date as base
      // But clamp so we never schedule in the past:
      // If baseDate + windowMinDay < today, shift baseDate forward so the
      // earliest candidate is today.
      baseDate = new Date(lastReviewDate);
      const earliestCandidate = addDays(baseDate, windowMinDay);
      if (earliestCandidate < todayStr) {
        // Recalculate: set baseDate so that baseDate + windowMinDay = today
        // i.e. baseDate = today - windowMinDay
        const adjustedBase = new Date(todayStr);
        adjustedBase?.setDate(adjustedBase?.getDate() - windowMinDay);
        baseDate = adjustedBase;
      }
    }

    // Get or create schedule map
    let localScheduleMap = scheduleMap;
    if (!localScheduleMap) {
      localScheduleMap = await getScheduleMap(userId);
    }

    // Find optimal day (uses least verses logic across the window)
    const nextReviewDate = findOptimalDay(localScheduleMap, baseDate, windowMinDay, windowMaxDay);

    // Update verse in database
    const { data: updatedVerse, error: updateError } = await supabase?.from('verses')?.update({ next_review_date: nextReviewDate })?.eq('id', verseId)?.select()?.single();

    if (updateError) {
      console.error('Error updating verse:', updateError);
      return { error: updateError?.message };
    }

    // Update schedule map
    const verseCount = updatedVerse?.verse_count || 1;
    if (!localScheduleMap?.[nextReviewDate]) {
      localScheduleMap[nextReviewDate] = 0;
    }
    localScheduleMap[nextReviewDate] += verseCount;

    return {
      success: true,
      verse: toCamelCase(updatedVerse),
      nextReviewDate,
    };
  } catch (error) {
    console.error('Error in scheduleVerse:', error);
    return { error: error?.message };
  }
};

/**
 * Schedule multiple verses in bulk
 * Iterates through verses and schedules each one, dynamically updating the map
 * Properly handles unreviewed verses with expanded windows
 * 
 * @param {Array<string>} verseIds - Array of verse IDs to schedule
 * @param {string} userId - User ID
 * @returns {Object} Results with success/error counts
 */
const scheduleBulkVerses = async (verseIds, userId) => {
  try {
    // Fetch all verses to schedule
    const { data: verses, error: fetchError } = await supabase?.from('verses')?.select('*')?.in('id', verseIds)?.eq('user_id', userId)?.gt('current_phase', 0); // Exclude Phase 0

    if (fetchError) {
      console.error('Error fetching verses for bulk scheduling:', fetchError);
      return { error: fetchError?.message };
    }

    if (!verses || verses?.length === 0) {
      return { error: 'No valid verses found to schedule' };
    }

    // Get initial schedule map
    const scheduleMap = await getScheduleMap(userId);

    // Track results
    const results = {
      total: verses?.length,
      successful: 0,
      failed: 0,
      updates: [],
      errors: [],
    };

    // Process each verse
    for (let verse of verses) {
      const verseData = toCamelCase(verse);
      // Pass null for lastReviewDate to let scheduleVerse detect unreviewed status
      let lastReviewDate = null;
      
      const result = await scheduleVerse(
        verseData?.id,
        verseData?.currentPhase,
        lastReviewDate,
        userId,
        scheduleMap // Pass shared map for dynamic updates
      );

      if (result?.success) {
        results.successful++;
        results?.updates?.push({
          verseId: verseData?.id,
          nextReviewDate: result?.nextReviewDate,
        });
      } else {
        results.failed++;
        results?.errors?.push({
          verseId: verseData?.id,
          error: result?.error,
        });
      }
    }

    return results;
  } catch (error) {
    console.error('Error in scheduleBulkVerses:', error);
    return { error: error?.message };
  }
};

// ============================================================================
// OPTIMIZED SCHEDULE OPTIMIZER
// ============================================================================

/**
 * Optimize existing schedule for a user — high-performance map-based approach.
 *
 * Algorithm:
 *  1. Fetch ALL user verses (phase > 0) into a Map keyed by id  — 1 DB read
 *  2. Build a slot map: day-offset 0–91 from today → verse count (integer array)
 *  3. For each verse compute its ideal window [minDay, maxDay] relative to today,
 *     pick the slot with the fewest verses (ties broken by earliest slot),
 *     assign the verse to that slot and increment the counter.
 *  4. Collect all (id, next_review_date) pairs and execute a single batched upsert.
 *
 * This replaces the old per-verse DB round-trip loop with a single read + single write.
 *
 * @param {string} userId - User ID
 * @param {boolean} optimizeAll - If true, reschedule all verses; if false, only overloaded days
 * @param {Function} onProgress - Optional callback(current, total) for progress reporting
 * @returns {Object} Optimization results
 */
const optimizeSchedule = async (userId, optimizeAll = schedulerConfig?.optimizeAllVerses, onProgress = null) => {
  try {
    // ── Step 1: Single fetch of all eligible verses ──────────────────────────
    const { data: rawVerses, error: fetchError } = await supabase?.from('verses')?.select('id, current_phase, last_reviewed_at, created_at, verse_count, next_review_date')?.eq('user_id', userId)?.gt('current_phase', 0);

    if (fetchError) {
      console.error('Error fetching verses for optimization:', fetchError);
      return { error: fetchError?.message };
    }

    if (!rawVerses || rawVerses?.length === 0) {
      return { message: 'No verses to optimize' };
    }

    // ── Step 2: Build verse map (id → verse) ─────────────────────────────────
    const verseMap = new Map();
    rawVerses?.forEach(v => verseMap?.set(v?.id, v));

    const todayStr = getTodayStr();
    const today = new Date(todayStr);

    // Determine which verses to reschedule
    let versesToReschedule = rawVerses;

    if (!optimizeAll) {
      // Get user's daily review limit
      const { data: profile } = await supabase?.from('user_profiles')?.select('daily_review_limit')?.eq('id', userId)?.single();

      const dailyLimit = profile?.daily_review_limit || 20;

      // Build current date→count map to find overloaded days
      const currentDateMap = {};
      rawVerses?.forEach(v => {
        if (v?.next_review_date) {
          currentDateMap[v.next_review_date] = (currentDateMap?.[v?.next_review_date] || 0) + (v?.verse_count || 1);
        }
      });

      const overloadedDates = new Set(
        Object.entries(currentDateMap)
          .filter(([, count]) => count > dailyLimit)
          .map(([date]) => date)
      );

      versesToReschedule = rawVerses?.filter(v => overloadedDates?.has(v?.next_review_date));

      if (versesToReschedule?.length === 0) {
        return { message: 'No overloaded days found. Schedule is already optimized.' };
      }
    }

    // ── Step 3: Build slot map (day 0–91 from today) ─────────────────────────
    // 91 covers Phase 7's max window (90 days) + 1 safety slot
    const SLOT_COUNT = 92;
    const slotMap = new Array(SLOT_COUNT)?.fill(0);

    // Pre-fill slot map with verses NOT being rescheduled
    if (!optimizeAll) {
      const rescheduleIds = new Set(versesToReschedule.map(v => v.id));
      rawVerses?.forEach(v => {
        if (!rescheduleIds?.has(v?.id) && v?.next_review_date) {
          const dayOffset = Math.round(
            (new Date(v.next_review_date) - today) / (1000 * 60 * 60 * 24)
          );
          if (dayOffset >= 0 && dayOffset < SLOT_COUNT) {
            slotMap[dayOffset] += (v?.verse_count || 1);
          }
        }
      });
    }

    // ── Step 4: Assign each verse to optimal slot ─────────────────────────────
    const updates = []; // { id, next_review_date }
    const errors = [];
    const total = versesToReschedule?.length;

    for (let i = 0; i < total; i++) {
      let verse = versesToReschedule?.[i];

      if (onProgress) onProgress(i, total);

      const phase = verse?.current_phase;
      const { windowMinDay, windowMaxDay } = calculateWindow(phase);
      let isUnreviewed = !verse?.last_reviewed_at;

      let slotMin, slotMax;

      if (isUnreviewed) {
        // Unreviewed: window is [0, windowMaxDay] from today
        slotMin = 0;
        slotMax = Math.min(windowMaxDay, SLOT_COUNT - 1);
      } else {
        // Reviewed: window is [windowMinDay, windowMaxDay] from last_reviewed_at
        // Convert to offsets from today
        const lastReviewed = new Date(verse.last_reviewed_at);
        const daysSinceReview = Math.round((today - lastReviewed) / (1000 * 60 * 60 * 24));

        // Earliest slot = windowMinDay days after last review, but never before today (slot 0)
        slotMin = Math.max(0, windowMinDay - daysSinceReview);
        // Latest slot = windowMaxDay days after last review
        slotMax = Math.max(slotMin, windowMaxDay - daysSinceReview);
        // Cap at slot array bounds
        slotMax = Math.min(slotMax, SLOT_COUNT - 1);
        slotMin = Math.min(slotMin, slotMax);
      }

      // Find slot with fewest verses in [slotMin, slotMax]
      let bestSlot = slotMin;
      let bestCount = slotMap?.[slotMin];
      for (let s = slotMin + 1; s <= slotMax; s++) {
        if (slotMap?.[s] < bestCount) {
          bestCount = slotMap?.[s];
          bestSlot = s;
        }
      }

      // Compute the actual date for this slot
      const targetDate = new Date(today);
      targetDate?.setDate(targetDate?.getDate() + bestSlot);
      const nextReviewDate = targetDate?.toISOString()?.split('T')?.[0];

      // Update slot map
      slotMap[bestSlot] += (verse?.verse_count || 1);

      updates?.push({ id: verse?.id, next_review_date: nextReviewDate, user_id: userId });
    }

    if (onProgress) onProgress(total, total);

    // ── Step 5: Single batched upsert ─────────────────────────────────────────
    // Supabase upsert in chunks of 500 to stay within request limits
    const CHUNK_SIZE = 500;
    let successful = 0;
    let failed = 0;

    for (let start = 0; start < updates?.length; start += CHUNK_SIZE) {
      const chunk = updates?.slice(start, start + CHUNK_SIZE);
      const results = await Promise.all(
        chunk?.map(({ id, next_review_date }) =>
          supabase?.from('verses')?.update({ next_review_date })?.eq('id', id)?.eq('user_id', userId)
        )
      );

      results?.forEach(({ error: updateError }) => {
        if (updateError) {
          console.error('Batch update error:', updateError);
          failed += 1;
          errors?.push({ error: updateError?.message });
        } else {
          successful += 1;
        }
      });
    }

    return {
      total,
      successful,
      failed,
      updates,
      errors,
    };
  } catch (error) {
    console.error('Error in optimizeSchedule:', error);
    return { error: error?.message };
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

export const schedulerService = {
  // Core scheduling functions
  scheduleVerse,
  scheduleBulkVerses,
  getScheduleMap,
  optimizeSchedule,

  // Configuration
  getConfig: () => ({ ...schedulerConfig }),
  updateConfig: (newConfig) => {
    Object.assign(schedulerConfig, newConfig);
  },
};
